import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DragAndDrop from "../../../components/molecules/DragAndDrop/DragAndDrop";
import Sidebar from "../../../components/molecules/Sidebar/Sidebar";
import "./Siniestros.scss";
import { formateoBasico, convertirFechaIsoAFormato, formatearHora, extraerHora } from "../../../utilidades/formateos";
import useGetDataUser from "../../../utilidades/customHooks/useGetDataUser";
import {
    getAccidentsKanban,
    getAccidentsCloseKanban,
    getAccidents,
    getAccidentsClose,
    crearDescuentoOperador,
    asignarDescuentoASiniestro,
    updateAccidents,
    getTipoSiniestro,
    getColaborators,
    getSeveridad,
    getChofer,
    getIncidentes,
    asignarIncidente,
    insertDescuento,
    getDescuentoOperadorById,
    updateDescuentoOperador,
    getGruposDesc,
    getHerramientasOperador,
    updateAccidentsFull,
    getSiniestroAdjuntos,
    uploadSiniestroFotos,
    deleteSiniestroFotoSlot,
    uploadSiniestroArchivos,
    deleteSiniestroArchivo,
    getTipoColision,
} from "./integracion";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Divider } from "primereact/divider";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { CircleMarker, useMapEvents, useMap, Popup } from "react-leaflet";
import Map from "../../../components/molecules/Map/Map";
import "leaflet/dist/leaflet.css";

// ── Helpers ────────────────────────────────────────────────────────────────────

// Formatos de fecha seguros → dd/mm/yyyy y dd/mm/yyyy HH:mm
const formatD = (iso) => { if (!iso) return ""; try { return convertirFechaIsoAFormato(iso); } catch { return String(iso); } };
const formatDT = (iso) => { if (!iso) return ""; try { return `${convertirFechaIsoAFormato(iso)} ${formatearHora(iso)}`; } catch { return String(iso); } };

const SEV_MAP = {
    LEVE: { icon: "pi-check-circle", color: "#107C10", bg: "#DFF6DD" },
    MODERADO: { icon: "pi-exclamation-circle", color: "#F7630C", bg: "#FFF4EC" },
    GRAVE: { icon: "pi-times-circle", color: "#D13438", bg: "#FDE7E9" },
    CRÍTICO: { icon: "pi-ban", color: "#740000", bg: "#FFD6D8" },
    CRITICO: { icon: "pi-ban", color: "#740000", bg: "#FFD6D8" },
};
const getSevConfig = (tipo) =>
    SEV_MAP[String(tipo ?? "").toUpperCase()] ?? { icon: "pi-circle", color: "#6b7280", bg: "#f3f4f6" };

const ESTATUS_MAP = {
    1: { label: "Nuevo", color: "#0078D4", bg: "#DEECF9" },
    2: { label: "En Proceso", color: "#F7630C", bg: "#FFF4EC" },
    3: { label: "Cerrado", color: "#107C10", bg: "#DFF6DD" },
};
const ESTATUS_OPTIONS = [
    { label: "Nuevo", value: 1 },
    { label: "En Proceso", value: 2 },
    { label: "Cerrado", value: 3 },
];
const getEstatusConfig = (id) =>
    ESTATUS_MAP[Number(id)] ?? { label: "N/D", color: "#6b7280", bg: "#f3f4f6" };

const statusToColumn = (estatus) => {


    const e = Number(estatus);
    if (e === 1) return "nuevo";
    if (e === 2) return "en_proceso";
    if (e === 3) return "cerrado";
    return "nuevo";
};

const COLUMN_DEFS = [
    { id: 1, title: "Nuevo / Reportado", className: "programado" },
    { id: 2, title: "En Proceso", className: "en-proceso" },
    { id: 3, title: "Cerrado", className: "completado" },
];

const DESCUENTO_FORM_EMPTY = {
    FECHA_FORMAT: new Date(),
    idGrupo: null,
    DESCUENTO_HERRAMIENTA: null,
    MONTO: null,
    DESCUENTO_PORCENTAJE: null,
    NO_DESCUENTOS: null,
    MONTO_DESCUENTO: null,
    periodoInicio: "",
    ID_REEMBOLSO: null,
    MOTIVO: "",
    ESTATUS: 1,
};

const DESCUENTO_PORCENTAJE_OPTIONS = [
    { label: "40%", value: 40 },
    { label: "50%", value: 50 },
    { label: "75%", value: 75 },
    { label: "100%", value: 100 },
];

const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
};

const ACCEPTED_FOTOS = ".jpg,.jpeg,.png,.webp";
const ACCEPTED_ARCHIVOS = ".pdf,.xls,.xlsx,.doc,.docx,.csv,.txt,.mp4,.mov,.avi";
const FOTO_SLOTS = [1, 2, 3, 4];
const ADJUNTOS_BASE_RAW = String(process.env.REACT_APP_ADJUNTOS_SINIESTROS ?? "").trim();

const getFileNameFromPath = (value) => {
    if (!value) return "";
    return String(value).split(/[\\/]/).pop() ?? String(value);
};

const formatFileSize = (bytes) => {
    const size = Number(bytes ?? 0);
    if (!Number.isFinite(size) || size <= 0) return "0 KB";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const isWindowsPhysicalPath = (value) => /^[a-zA-Z]:\\/.test(String(value ?? ""));
const isHttpUrl = (value) => /^https?:\/\//i.test(String(value ?? ""));
const normalizePathSlashes = (value) => String(value ?? "").replace(/\\/g, "/");
const trimTrailingSlash = (value) => String(value ?? "").replace(/\/+$/, "");
const trimLeadingSlash = (value) => String(value ?? "").replace(/^\/+/, "");

const resolveAdjuntosBasePublic = () => {
    if (!ADJUNTOS_BASE_RAW) return "";
    if (isHttpUrl(ADJUNTOS_BASE_RAW) || ADJUNTOS_BASE_RAW.startsWith("/")) {
        return trimTrailingSlash(ADJUNTOS_BASE_RAW);
    }
    if (isWindowsPhysicalPath(ADJUNTOS_BASE_RAW)) {
        const folder = getFileNameFromPath(ADJUNTOS_BASE_RAW);
        return folder ? `/${folder}` : "";
    }
    return `/${trimLeadingSlash(trimTrailingSlash(ADJUNTOS_BASE_RAW))}`;
};

const ADJUNTOS_BASE_PUBLIC = resolveAdjuntosBasePublic();

const getRelativeAdjuntoPath = (candidate) => {
    const normalizedCandidate = normalizePathSlashes(candidate).trim();
    if (!normalizedCandidate) return "";

    if (isHttpUrl(normalizedCandidate)) return normalizedCandidate;

    if (ADJUNTOS_BASE_RAW) {
        const normalizedBase = trimTrailingSlash(normalizePathSlashes(ADJUNTOS_BASE_RAW));
        if (normalizedBase) {
            const lowerCandidate = normalizedCandidate.toLowerCase();
            const lowerBase = normalizedBase.toLowerCase();
            if (lowerCandidate.startsWith(lowerBase)) {
                return trimLeadingSlash(normalizedCandidate.slice(normalizedBase.length));
            }
        }
    }

    return trimLeadingSlash(normalizedCandidate);
};

const getAdjuntoHref = (row) => {
    const candidate = row?.publicUrl
        ?? row?.url
        ?? row?.URL
        ?? row?.archivoUrl
        ?? row?.ARCHIVO_URL
        ?? row?.path
        ?? row?.PATH
        ?? row?.ruta
        ?? row?.RUTA
        ?? row?.archivo
        ?? row?.ARCHIVO
        ?? "";
    if (!candidate) return "";

    const asString = String(candidate);
    if (isHttpUrl(asString)) return asString;

    const relativeOrUrl = getRelativeAdjuntoPath(asString);
    if (!relativeOrUrl) return "";
    if (isHttpUrl(relativeOrUrl)) return relativeOrUrl;

    if (ADJUNTOS_BASE_PUBLIC) {
        return `${ADJUNTOS_BASE_PUBLIC}/${trimLeadingSlash(relativeOrUrl)}`;
    }

    if (isWindowsPhysicalPath(asString)) return "";
    return asString;
};

const isAdjuntoFisicamenteDisponible = (row) => {
    if (!row) return false;
    const explicit = row.exists
        ?? row.EXISTS
        ?? row.existe
        ?? row.EXISTE
        ?? row.existeFisico
        ?? row.EXISTE_FISICO
        ?? row.existe_fisico
        ?? row.fileExists
        ?? row.FILE_EXISTS
        ?? row.archivoExiste
        ?? row.archivo_existe;
    if (explicit !== undefined && explicit !== null) {
        if (typeof explicit === "string") {
            const normalized = explicit.trim().toLowerCase();
            if (["false", "0", "no", "n"].includes(normalized)) return false;
            if (["true", "1", "si", "sí", "y", "yes"].includes(normalized)) return true;
        }
        if (typeof explicit === "number") return explicit > 0;
        return !!explicit;
    }

    // Si no hay bandera explícita, asumimos disponible sólo cuando hay URL/ruta utilizable.
    return !!getAdjuntoHref(row);
};

const normalizeFotoSlot = (slot, row) => {
    if (!row || !isAdjuntoFisicamenteDisponible(row)) return null;
    const href = getAdjuntoHref(row);
    const path = row?.path ?? row?.ruta ?? row?.archivo ?? href;
    if (!path) return null;
    return {
        slot,
        name: row?.name ?? row?.nombre ?? getFileNameFromPath(path),
        path,
        previewUrl: href,
        isRemote: true,
        file: null,
    };
};

const buildFotoSlotsFromSiniestro = (row) =>
    FOTO_SLOTS.map((slot) => {
        const key = `ARCHIVO_FOTOGRAFICO_${slot}`;
        const path = row?.[key];
        if (!path) return null;
        return {
            slot,
            name: getFileNameFromPath(path),
            path,
            previewUrl: path,
            isRemote: true,
            file: null,
        };
    });

const normalizeSiniestrosResponse = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.canban)) return res.canban;
    if (Array.isArray(res?.table)) return res.table;
    if (Array.isArray(res?.data)) return res.data;
    return [];
};

const normalizeTiposColision = (items) => (Array.isArray(items) ? items : [])
    .map((item) => ({
        ...item,
        label: item.label ?? item.name ?? item.NOMBRE ?? item.TIPO_COLISION ?? item.DESCRIPCION,
        value: String(item.value ?? item.code ?? item.ID_TIPO_COLISION ?? item.ID ?? item.id ?? ""),
    }))
    .filter((item) => item.value && item.label);

const resolveTipoColisionValue = (data, options) => {
    if (!data) return null;
    const currentId = data.ID_TIPO_COLISION;
    const byId = options.find((option) => String(option.value) === String(currentId));
    if (byId) return byId.value;

    const currentLabel = String(data.TIPO_COLISION ?? "").trim().toUpperCase();
    const byLabel = options.find((option) => String(option.label).trim().toUpperCase() === currentLabel);
    return byLabel?.value ?? null;
};

// Convierte un Date/string a ISO UTC inicio del día
const toIsoUtcStartOfDay = (input) => {
    if (!input) return null;
    if (input instanceof Date && !isNaN(input)) {
        const d = new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate(), 0, 0, 0, 0));
        return d.toISOString();
    }
    if (typeof input === 'string') {
        const s = input.trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
            const [dd, mm, yyyy] = s.split('/').map(Number);
            const d = new Date(Date.UTC(yyyy, mm - 1, dd, 0, 0, 0, 0));
            return isNaN(d) ? null : d.toISOString();
        }
        const parsed = new Date(s);
        if (!isNaN(parsed)) {
            const d = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 0, 0, 0, 0));
            return d.toISOString();
        }
    }
    return null;
};

const parseGoogleMapsUrl = (url) => {
    if (!url) return null;
    // /@lat,lng pattern (la mayoría de URLs de Google Maps)
    const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atMatch) return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
    // ?q=lat,lng
    const qMatch = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (qMatch) return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };
    // ll=lat,lng
    const llMatch = url.match(/ll=(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (llMatch) return { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]) };
    return null;
};

const normalizeToDate = (sOrDate) => {
    if (!sOrDate) return null;
    if (sOrDate instanceof Date) return !isNaN(sOrDate) ? sOrDate : null;
    if (typeof sOrDate === "string") {
        const s = sOrDate.trim();
        // dd/MM/yyyy  ó  dd/MM/yyyy HH:mm:ss
        if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
            const [dd, mm, yyyy] = s.split(' ')[0].split('/').map(Number);
            const d = new Date(yyyy, mm - 1, dd);
            return isNaN(d) ? null : d;
        }
        // ISO yyyy-MM-dd (con o sin hora/zona) — extraer partes en local para evitar desfase UTC
        const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const d = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
            return isNaN(d) ? null : d;
        }
    }
    return null;
};



// ── Colores de columna por estatus ────────────────────────────────────────────
const COL_MAP = {
    1: { color: "#F59E0B", bg: "rgba(245,158,11,0.05)" },  // nuevo — ámbar
    2: { color: "#3B82F6", bg: "rgba(59,130,246,0.04)" }, // en proceso — azul
    3: { color: "#22C55E", bg: "rgba(34,197,94, 0.04)" }, // cerrado — verde
};
const getColConfig = (estatus) =>
    COL_MAP[Number(estatus)] ?? { color: "#94a3b8", bg: "rgba(0,0,0,0.02)" };

// ── Tarjeta Kanban ─────────────────────────────────────────────────────────────

const SiniestroCard = ({ item }) => {
    const sev = getSevConfig(item.TIPO_SEVERIDAD);
    const col = getColConfig(item.ESTATUS_SINIESTRO);
    const folio = `SINIESTRO - ${item.ID}`;
    return (
        <div className="sin-card" style={{ background: col.bg }}>

            {/* Header: folio + severity badge */}
            <div className="sin-card__header">
                <span className="sin-card__folio">{folio}</span>
                <span className="sin-card__sev-badge" style={{ color: sev.color, background: sev.bg }}>
                    <i className={`pi ${sev.icon}`} />
                    {item.TIPO_SEVERIDAD ?? "—"}
                </span>
            </div>

            {/* Tipo — descripción prominente */}
            <div className="sin-card__tipo">
                {item.TIPO_SINIESTRO_DESCRIPCION ?? item.TIPO_SINIESTRO ?? "Sin tipo"}
            </div>

            {/* Fecha + Placa como píldoras */}
            <div className="sin-card__pills">
                <span className="sin-card__pill">
                    <i className="pi pi-calendar" />
                    {formatD(item.FECHA_SINIESTRO) || "—"}
                </span>
                <span className="sin-card__pill">
                    <i className="pi pi-car" />
                    {item.UNIDAD_PLACAS ?? item.PLACA_UNIDAD ?? "—"}
                </span>
            </div>

            {/* Operador */}
            <div className="sin-card__operator">
                <i className="pi pi-user" />
                <span>{item.OPERADOR ?? item.NOMBRE_CHOFER ?? "—"}</span>
            </div>

            {/* Footer: incidencia + descuento */}
            <div className="sin-card__footer">
                <span className={`sin-card__chip ${item.ID_INCIDENTE ? "sin-card__chip--linked" : "sin-card__chip--null"}`}>
                    <i className={`pi ${item.ID_INCIDENTE ? "pi-link" : "pi-minus-circle"}`} />
                    {item.ID_INCIDENTE ? `Inc. #${item.ID_INCIDENTE}` : "Sin incidencia asignada"}
                </span>
                <span className={`sin-card__chip ${item.ID_DESCUENTO ? "sin-card__chip--descuento" : "sin-card__chip--null"}`}>
                    <i className={`pi ${item.ID_DESCUENTO ? "pi-dollar" : "pi-minus-circle"}`} />
                    {item.ID_DESCUENTO ? `Desc. #${item.ID_DESCUENTO}` : "Sin descuento"}
                </span>
            </div>
        </div>
    );
};

// ── Campo de lectura / edición para el sidebar ────────────────────────────────

const EditableField = ({ label, value, onChange, full, type = "text", options = [], optionLabel = "label", optionValue = "value" }) => {
    const cls = `sin-field${full ? " sin-field--full" : ""}`;
    return (
        <div className={cls}>
            <label className="sin-field__label">{label}</label>
            {onChange ? (
                type === "date" ? (
                    <Calendar
                        value={value ? new Date(value) : null}
                        onChange={(e) => onChange(e.value)}
                        showIcon
                        dateFormat="dd/mm/yy"
                        className="sin-sidebar__input w-full"
                    />
                ) : type === "number" ? (
                    <InputNumber
                        value={value ?? null}
                        onValueChange={(e) => onChange(e.value)}
                        className="sin-sidebar__input w-full"
                    />
                ) : type === "checkbox" ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                        <Checkbox checked={!!value} onChange={(e) => onChange(e.checked)} />
                        <span className="sin-field__value">{value ? "Sí" : "No"}</span>
                    </div>
                ) : type === "dropdown" ? (
                    <Dropdown
                        value={value ?? null}
                        options={options}
                        optionLabel={optionLabel}
                        optionValue={optionValue}
                        onChange={(e) => onChange(e.value)}
                        filter
                        className="sin-sidebar__input w-full"
                    />
                ) : (
                    <InputText
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value)}
                        className="sin-sidebar__input w-full"
                    />
                )
            ) : (
                <span className="sin-field__value">
                    {value != null && value !== "" ? value : <em className="sin-field__null">—</em>}
                </span>
            )}
        </div>
    );
};

// ── Mini-mapa de ubicación ────────────────────────────────────────────────────

const MapClickHandler = ({ onPick }) => {
    useMapEvents({ click: (e) => onPick && onPick(e.latlng.lat, e.latlng.lng) });
    return null;
};

const MapResizeHandler = ({ resizeTick }) => {
    const map = useMap();

    useEffect(() => {
        // Forzar recálculo de tamaño al cambiar dimensiones del sidebar.
        const rafId = requestAnimationFrame(() => map.invalidateSize());
        const timeoutId = setTimeout(() => map.invalidateSize(), 180);

        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(timeoutId);
        };
    }, [map, resizeTick]);

    return null;
};

const SidebarMap = ({ lat, lng, editable, onPick }) => {
    const mapWrapRef = useRef(null);
    const [resizeTick, setResizeTick] = useState(0);
    const hasCoords = lat != null && lng != null && lat !== "" && lng !== "";
    const center = hasCoords ? [parseFloat(lat), parseFloat(lng)] : [20.6597, -103.3496]; // Guadalajara

    useEffect(() => {
        if (!mapWrapRef.current || typeof ResizeObserver === "undefined") return;

        let lastW = 0;
        let lastH = 0;

        const observer = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (!rect) return;

            const w = Math.round(rect.width);
            const h = Math.round(rect.height);

            if (w !== lastW || h !== lastH) {
                lastW = w;
                lastH = h;
                setResizeTick((prev) => prev + 1);
            }
        });

        observer.observe(mapWrapRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="sin-sidebar__map-wrap" ref={mapWrapRef}>
            <Map
                key={`smap-${lat}-${lng}`}
                center={center}
                zoom={hasCoords ? 15 : 11}
                style={{ height: "400px", borderRadius: "8px", zIndex: 0 }}
                scrollWheelZoom
            >
                <MapResizeHandler resizeTick={resizeTick} />
                {hasCoords && (
                    <CircleMarker
                        center={center}
                        radius={9}
                        color="#D13438"
                        fillColor="#D13438"
                        fillOpacity={0.85}
                        weight={2}
                    />
                )}
                {editable && <MapClickHandler onPick={onPick} />}
            </Map>
            <p className="sin-sidebar__map-hint">
                {!hasCoords
                    ? editable
                        ? "📍 Haz clic en el mapa para registrar la ubicación del siniestro"
                        : "Sin coordenadas registradas"
                    : `📍 ${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`}
            </p>
        </div>
    );
};

// ── Vista: Tabla ───────────────────────────────────────────────────────────────

const TablaView = ({ siniestros, onVerDetalle }) => {
    const folioBody = (r) => `SIN-${String(r.ID ?? "").padStart(4, "0")}`;
    const fechaBody = (r) => formatD(r.FECHA_SINIESTRO) || "—";
    const sevBody = (r) => {
        const sev = getSevConfig(r.TIPO_SEVERIDAD);
        return (
            <span className="sin-sev-pill" style={{ color: sev.color, background: sev.bg }}>
                <i className={`pi ${sev.icon}`} />{r.TIPO_SEVERIDAD ?? "—"}
            </span>
        );
    };
    const estBody = (r) => {
        const est = getEstatusConfig(r.ESTATUS_SINIESTRO);
        return (
            <span className="sin-est-pill" style={{ color: est.color, background: est.bg }}>
                {est.label}
            </span>
        );
    };
    return (
        <DataTable
            value={siniestros}
            onRowClick={(e) => onVerDetalle(e.data)}
            selectionMode="single"
            rowHover
            className="sin-datatable"
            scrollable
            scrollHeight="calc(100vh - 210px)"
            size="small"
            emptyMessage="Sin registros"
        >
            <Column header="Folio" body={folioBody} style={{ minWidth: "100px" }} />
            <Column field="TIPO_SINIESTRO_DESCRIPCION" header="Tipo" style={{ minWidth: "160px" }} sortable />
            <Column header="Severidad" body={sevBody} field="TIPO_SEVERIDAD" style={{ minWidth: "130px" }} sortable />
            <Column header="Fecha" body={fechaBody} field="FECHA_SINIESTRO" style={{ minWidth: "110px" }} sortable />
            <Column header="Operador" body={(r) => r.OPERADOR ?? r.NOMBRE_CHOFER ?? "—"} style={{ minWidth: "180px" }} sortable />
            <Column header="Estatus" body={estBody} field="ESTATUS_SINIESTRO" style={{ minWidth: "130px" }} sortable />
        </DataTable>
    );
};

// ── Vista: Mapa de calor ───────────────────────────────────────────────────────

// A=Ordinario  B=Menor  C=Moderado  D=Mayor  E=Crítico
const SEV_COLOR_HEAT = {
    A: "#6B7280",   // Ordinario — gris
    B: "#22C55E",   // Menor     — verde
    C: "#F59E0B",   // Moderado  — ámbar
    D: "#EF4444",   // Mayor     — rojo
    E: "#7C3F00",   // Crítico   — rojo oscuro
};

const SEV_LABEL_HEAT = {
    A: "Ordinario",
    B: "Menor",
    C: "Moderado",
    D: "Mayor",
    E: "Crítico",
};

const HeatmapView = ({ siniestros }) => {
    // Normaliza coordenadas: Kanban usa LAT/LNG, detalle usa LATITUD/LONGITUD
    const getLat = (s) => s.LAT ?? s.LATITUD ?? null;
    const getLng = (s) => s.LNG ?? s.LONGITUD ?? null;

    // Normaliza TIPO_SEVERIDAD a la clave de letra (A-E)
    const getSevColor = (s) => {
        const raw = String(s.TIPO_SEVERIDAD ?? "").trim().toUpperCase();
        // Si ya viene como letra (A-E) úsala directamente
        if (SEV_COLOR_HEAT[raw]) return SEV_COLOR_HEAT[raw];
        // Fallback por ALCANCE_SEVERIDAD (descripción)
        const desc = String(s.ALCANCE_SEVERIDAD ?? "").trim().toUpperCase();
        const byDesc = Object.entries(SEV_LABEL_HEAT).find(([, v]) => desc.includes(v.toUpperCase()));
        return byDesc ? SEV_COLOR_HEAT[byDesc[0]] : "#6B7280";
    };

    const getSevLabel = (s) => {
        const raw = String(s.TIPO_SEVERIDAD ?? "").trim().toUpperCase();
        return SEV_LABEL_HEAT[raw] ?? s.ALCANCE_SEVERIDAD ?? raw;
    };

    const points = useMemo(
        () => siniestros.filter((s) => getLat(s) && getLng(s)),
        [siniestros]
    );
    const center = points.length > 0
        ? [parseFloat(getLat(points[0])), parseFloat(getLng(points[0]))]
        : [19.4326, -99.1332];

    return (
        <div className="sin-heatmap">
            <div className="sin-heatmap__legend">
                {Object.entries(SEV_LABEL_HEAT).map(([k, label]) => (
                    <span key={k} className="sin-heatmap__leg-item">
                        <span className="sin-heatmap__dot" style={{ background: SEV_COLOR_HEAT[k] }} />
                        {label}
                    </span>
                ))}
                <span className="sin-heatmap__leg-count">
                    {points.length} siniestro{points.length !== 1 ? "s" : ""} con coordenadas
                </span>
            </div>

            {points.length === 0 ? (
                <div className="sin-heatmap__empty">
                    <i className="pi pi-map" style={{ fontSize: "2.5rem", color: "#94a3b8" }} />
                    <p>Ningún siniestro tiene coordenadas registradas.</p>
                    <p>Edita un registro desde Kanban o Tabla para añadir la ubicación.</p>
                </div>
            ) : (
                <Map
                    center={center}
                    zoom={12}
                    style={{ height: "calc(100vh - 230px)", borderRadius: "12px" }}
                    scrollWheelZoom
                >
                    {points.map((s) => {
                        const c = getSevColor(s);
                        const pos = [parseFloat(getLat(s)), parseFloat(getLng(s))];
                        const folio = `SIN-${String(s.ID ?? "").padStart(4, "0")}`;
                        const operador = s.OPERADOR ?? s.NOMBRE_CHOFER ?? "—";
                        const sevLabel = getSevLabel(s);
                        return (
                            <React.Fragment key={s.ID}>
                                <CircleMarker center={pos} radius={38} color="transparent" fillColor={c} fillOpacity={0.06} />
                                <CircleMarker center={pos} radius={20} color="transparent" fillColor={c} fillOpacity={0.13} />
                                <CircleMarker center={pos} radius={8} color={c} fillColor={c} fillOpacity={0.9} weight={2}>
                                    <Popup>
                                        <strong>{folio}</strong><br />
                                        {s.TIPO_SINIESTRO_DESCRIPCION}<br />
                                        <em>{sevLabel}</em> · {operador}<br />
                                        {formatD(s.FECHA_SINIESTRO)}
                                    </Popup>
                                </CircleMarker>
                            </React.Fragment>
                        );
                    })}
                </Map>
            )}
        </div>
    );
};

// ── Componente principal ───────────────────────────────────────────────────────

const Siniestros = () => {
    const currentMonthRange = getCurrentMonthRange();
    const [listas, setListas] = useState(COLUMN_DEFS.map((c) => ({ ...c, items: [] })));
    const [siniestrosCanban, setSiniestrosCanban] = useState();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [siniestroSel, setSiniestroSel] = useState(null);
    const [filtros, setFiltros] = useState({ fecha_inicio: currentMonthRange.start, fecha_fin: currentMonthRange.end });
    // Vista activa
    const [activeView, setActiveView] = useState("kanban"); // "kanban" | "tabla" | "heatmap"

    // Modo edición en sidebar
    const [editData, setEditData] = useState(null); // null = sólo lectura

    // Incidencias
    const [incidentesDisponibles, setIncidentesDisponibles] = useState([]);
    const [loadingInc, setLoadingInc] = useState(false);

    // Formulario de descuento
    const [showDescForm, setShowDescForm] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [descForm, setDescForm] = useState(DESCUENTO_FORM_EMPTY);
    const [savingDesc, setSavingDesc] = useState(false);
    const [savingInc, setSavingInc] = useState(false);
    // opciones catálogos
    const [tiposSiniestro, setTiposSiniestro] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [severidades, setSeveridades] = useState([]);
    const [choferes, setChoferes] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [herramientas, setHerramientas] = useState([]);

    // Adjuntos del siniestro
    const [adjuntosLoading, setAdjuntosLoading] = useState(false);
    const [fotoSlots, setFotoSlots] = useState([null, null, null, null]);
    const [archivosDinamicos, setArchivosDinamicos] = useState([]);
    const [archivosPendientes, setArchivosPendientes] = useState([]);
    const [archivoAnalisis, setArchivoAnalisis] = useState(null);
    const [savingAdjuntos, setSavingAdjuntos] = useState(false);
    const [mapsUrlInput, setMapsUrlInput] = useState("");
    const [tiposColision, setTiposColision] = useState([]);
    const localBlobUrlsRef = useRef(new Set());

    const dataUser = useGetDataUser();
    const toast = useRef(null);

    const showToast = useCallback((severity, summary, detail, life = 3500) => {
        toast.current?.show({ severity, summary, detail, life });
    }, []);

    const registerBlobUrl = useCallback((url) => {
        if (url?.startsWith("blob:")) localBlobUrlsRef.current.add(url);
    }, []);

    const revokeBlobUrl = useCallback((url) => {
        if (!url?.startsWith("blob:")) return;
        if (localBlobUrlsRef.current.has(url)) {
            URL.revokeObjectURL(url);
            localBlobUrlsRef.current.delete(url);
        }
    }, []);

    useEffect(() => {
        return () => {
            localBlobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            localBlobUrlsRef.current.clear();
        };
    }, []);

    // ── Distribuir items en columnas ──────────────────────────────────────────
    useEffect(() => {
        filtrar();
        Promise.all([
            getTipoSiniestro(), getColaborators(), getSeveridad(), getChofer(),
            getGruposDesc(), getHerramientasOperador(),
        ]).then(([tipos, colab, sev, chof, grps, herra]) => {
            setTiposSiniestro(tipos ?? []);
            setColaboradores(colab ?? []);
            setSeveridades(sev ?? []);
            setChoferes(chof ?? []);
            setGrupos(grps ?? []);
            setHerramientas(herra ?? []);
        });
        getTipoColision().then((res) => {
            setTiposColision(normalizeTiposColision(res));
        })
            .catch((error) => {
                console.log("error setTiposColision", error);

            })

    }, []);

    // Auto-cálculo monto por descuento cuando cambia monto o porcentaje
    useEffect(() => {
        if (descForm.DESCUENTO_PORCENTAJE != null && descForm.MONTO != null) {
            const pct = Number(descForm.DESCUENTO_PORCENTAJE) / 100;
            const calc = Math.round((descForm.MONTO * pct + Number.EPSILON) * 100) / 100;
            setDescForm(f => ({ ...f, MONTO_DESCUENTO: calc }));
        }
    }, [descForm.MONTO, descForm.DESCUENTO_PORCENTAJE]);

    useEffect(() => {
        setListas(
            COLUMN_DEFS.map((col) => ({
                ...col,
                items: (siniestrosCanban ?? [])
                    .filter((s) => Number(s.ESTATUS_SINIESTRO) === col.id)
                    .map((s) => ({ ...s, id: s.ID })),
            }))
        );
    }, [siniestrosCanban]);

    const filtrar = async () => {
        try {
            const paramsClose = {
                fecha_inicio: formateoBasico(filtros.fecha_inicio),
                fecha_fin: formateoBasico(filtros.fecha_fin),
            };

            const [abiertosRes, cerradosRes] = await Promise.all([
                getAccidentsKanban(),
                getAccidentsCloseKanban(paramsClose),
            ]);

            const abiertos = normalizeSiniestrosResponse(abiertosRes);
            const cerrados = normalizeSiniestrosResponse(cerradosRes);
            const merged = [...abiertos, ...cerrados];
            setSiniestrosCanban(merged);
            return merged;
        } catch (error) {
            showToast("error", "Error", "No se pudieron cargar los siniestros.");
            return [];
        }
    };

    const handleItemMove = async (item, fromListId, toListId) => {
        const params = {
            id: item.ID,
            estatus: toListId,
        }
        try {
            await updateAccidents(params);
            await refreshSiniestroAfterUpdate(item.ID, toListId);
            showToast("success", "Estatus actualizado", "El siniestro cambió de columna correctamente.", 2500);
        } catch (error) {
            showToast("error", "Error", "No se pudo actualizar el estatus del siniestro.");
        }
    };

    const refreshSiniestroAfterUpdate = useCallback(async (idSiniestro, estatusSiniestro) => {
        if (!idSiniestro) return null;

        await filtrar();

        const isClosed = Number(estatusSiniestro) === 3;
        const detalleRes = isClosed
            ? await getAccidentsClose({ id: idSiniestro })
            : await getAccidents({ id: idSiniestro });

        const detalle = Array.isArray(detalleRes)
            ? detalleRes[0]
            : detalleRes?.data?.[0] ?? detalleRes?.[0] ?? detalleRes?.data ?? detalleRes ?? null;

        if (detalle) {
            setSiniestroSel((prev) => (prev ? { ...prev, ...detalle } : detalle));
            return detalle;
        }

        return null;
    }, []);

    const handleVerDetalle = useCallback(async (item) => {
        let selected = item;
        setSidebarOpen(true);
        setEditData(null);
        setShowDescForm(false);
        setEditingDesc(false);
        setDescForm(DESCUENTO_FORM_EMPTY);
        setIncidentesDisponibles([]);
        setArchivosPendientes([]);
        setArchivosDinamicos([]);
        setArchivoAnalisis(null);
        setFotoSlots([null, null, null, null]);

        setAdjuntosLoading(true);
        try {
            const isClosed = Number(item?.ESTATUS_SINIESTRO) === 3;
            const detalleRes = isClosed
                ? await getAccidentsClose({ id: item.ID })
                : await getAccidents({ id: item.ID });

            const detalle = Array.isArray(detalleRes)
                ? detalleRes[0]
                : detalleRes?.data?.[0] ?? detalleRes?.[0] ?? detalleRes?.data ?? detalleRes ?? item;

            selected = detalle ? { ...item, ...detalle } : item;
            setSiniestroSel(selected);

            const resAdj = await getSiniestroAdjuntos(item.ID);
            const payload = resAdj?.data ?? resAdj ?? {};

            const incomingFotos = Array.isArray(payload.fotos) ? payload.fotos : [];
            const normalizedFotos = FOTO_SLOTS.map((slot) => {
                const row = incomingFotos.find((f) => Number(f?.slot) === slot || Number(f?.index) === slot);
                return normalizeFotoSlot(slot, row);
            });

            const hasAnyFoto = normalizedFotos.some(Boolean);
            if (hasAnyFoto) {
                setFotoSlots(normalizedFotos);
            }

            const archivos = Array.isArray(payload.archivos) ? payload.archivos : [];
            setArchivosDinamicos(archivos.filter(isAdjuntoFisicamenteDisponible));

            const analisis = payload?.analisis ?? payload?.archivoAnalisis ?? null;
            if (isAdjuntoFisicamenteDisponible(analisis)) {
                setArchivoAnalisis(analisis);
            }
        } catch {
            // Si el endpoint falla, evitamos renderizar adjuntos no verificados físicamente.
            setArchivosDinamicos([]);
            setArchivoAnalisis(null);
        } finally {
            setAdjuntosLoading(false);
        }

        if (selected?.ID_UNIDAD && selected?.FECHA_SINIESTRO) {
            setLoadingInc(true);
            try {
                const fecha = formateoBasico(selected.FECHA_SINIESTRO);
                const inc = await getIncidentes(fecha, selected.ID_UNIDAD);
                setIncidentesDisponibles(inc ?? []);
            } finally {
                setLoadingInc(false);
            }
        }
    }, []);

    const syncSiniestroFotoFields = useCallback((slots) => {
        const patch = {
            ARCHIVO_FOTOGRAFICO_1: slots?.[0]?.path ?? null,
            ARCHIVO_FOTOGRAFICO_2: slots?.[1]?.path ?? null,
            ARCHIVO_FOTOGRAFICO_3: slots?.[2]?.path ?? null,
            ARCHIVO_FOTOGRAFICO_4: slots?.[3]?.path ?? null,
        };
        setSiniestroSel((prev) => (prev ? { ...prev, ...patch } : prev));
        setSiniestrosCanban((prev) => (prev ?? []).map((row) => (row.ID === siniestroSel?.ID ? { ...row, ...patch } : row)));
    }, [siniestroSel?.ID]);

    const resetFotoSlot = useCallback((slot) => {
        const idx = slot - 1;
        setFotoSlots((prev) => {
            const next = [...prev];
            const prevItem = next[idx];
            if (prevItem?.previewUrl) revokeBlobUrl(prevItem.previewUrl);
            next[idx] = null;
            return next;
        });
    }, [revokeBlobUrl]);

    const onSelectFoto = useCallback((slot, file) => {
        if (!file) return;
        const idx = slot - 1;
        const previewUrl = URL.createObjectURL(file);
        registerBlobUrl(previewUrl);
        setFotoSlots((prev) => {
            const next = [...prev];
            const prevItem = next[idx];
            if (prevItem?.previewUrl) revokeBlobUrl(prevItem.previewUrl);
            next[idx] = {
                slot,
                name: file.name,
                path: null,
                previewUrl,
                isRemote: false,
                file,
            };
            return next;
        });
    }, [registerBlobUrl, revokeBlobUrl]);

    const handleGuardarFotos = useCallback(async () => {
        if (!siniestroSel?.ID) return;
        const fotosPorSubir = fotoSlots.filter((f) => f?.file);
        if (fotosPorSubir.length === 0) {
            showToast("warn", "Sin cambios", "No hay fotos nuevas para subir.");
            return;
        }

        setSavingAdjuntos(true);
        try {
            const formData = new FormData();
            fotosPorSubir.forEach((foto) => {
                formData.append(`foto_${foto.slot}`, foto.file);
                formData.append("slots[]", String(foto.slot));
            });
            formData.append("id_control_siniestro", String(siniestroSel.ID));
            formData.append("usuario", String(dataUser?.user?.ID ?? ""));

            const resp = await uploadSiniestroFotos(siniestroSel.ID, formData);
            const payload = resp?.data ?? resp ?? {};
            const incomingFotos = Array.isArray(payload.fotos) ? payload.fotos : [];
            if (incomingFotos.length > 0) {
                const normalized = FOTO_SLOTS.map((slot) => {
                    const row = incomingFotos.find((f) => Number(f?.slot) === slot || Number(f?.index) === slot);
                    return normalizeFotoSlot(slot, row);
                });
                setFotoSlots((prev) => {
                    prev.forEach((x) => x?.previewUrl && revokeBlobUrl(x.previewUrl));
                    return normalized;
                });
                syncSiniestroFotoFields(normalized);
            } else {
                // fallback optimista si backend responde sin payload de fotos
                setFotoSlots((prev) => {
                    const next = prev.map((x) => {
                        if (!x?.file) return x;
                        return {
                            ...x,
                            path: x.name,
                            previewUrl: x.previewUrl,
                            isRemote: true,
                            file: null,
                        };
                    });
                    syncSiniestroFotoFields(next);
                    return next;
                });
            }
            await refreshSiniestroAfterUpdate(siniestroSel.ID, siniestroSel.ESTATUS_SINIESTRO);
            showToast("success", "Fotos guardadas", "Las fotos del siniestro se actualizaron correctamente.");
        } catch {
            showToast("error", "Error", "No se pudieron guardar las fotos del siniestro.");
        } finally {
            setSavingAdjuntos(false);
        }
    }, [dataUser?.user?.ID, fotoSlots, revokeBlobUrl, showToast, siniestroSel?.ID, syncSiniestroFotoFields]);

    const handleEliminarFoto = useCallback(async (slot) => {
        if (!siniestroSel?.ID) return;
        const current = fotoSlots?.[slot - 1];
        if (!current) return;

        if (!current.isRemote) {
            resetFotoSlot(slot);
            return;
        }

        setSavingAdjuntos(true);
        try {
            await deleteSiniestroFotoSlot(siniestroSel.ID, slot);
            setFotoSlots((prev) => {
                const next = [...prev];
                next[slot - 1] = null;
                syncSiniestroFotoFields(next);
                return next;
            });
            await refreshSiniestroAfterUpdate(siniestroSel.ID, siniestroSel.ESTATUS_SINIESTRO);
            showToast("success", "Foto eliminada", `Se eliminó la foto ${slot}.`, 2500);
        } catch {
            showToast("error", "Error", "No se pudo eliminar la foto seleccionada.");
        } finally {
            setSavingAdjuntos(false);
        }
    }, [fotoSlots, resetFotoSlot, showToast, siniestroSel?.ID, syncSiniestroFotoFields]);

    const handleAddArchivosPendientes = useCallback((files) => {
        const incoming = Array.from(files ?? []);
        if (incoming.length === 0) return;
        setArchivosPendientes((prev) => {
            const seen = new Set(prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
            const merged = [...prev];
            incoming.forEach((f) => {
                const key = `${f.name}-${f.size}-${f.lastModified}`;
                if (!seen.has(key)) {
                    merged.push(f);
                    seen.add(key);
                }
            });
            return merged;
        });
    }, []);

    const handleUploadArchivos = useCallback(async () => {
        if (!siniestroSel?.ID) return;
        if (archivosPendientes.length === 0) {
            showToast("warn", "Sin cambios", "No hay archivos pendientes para subir.");
            return;
        }

        setSavingAdjuntos(true);
        try {
            const formData = new FormData();
            archivosPendientes.forEach((file) => {
                formData.append("archivos", file);
            });
            formData.append("id_control_siniestro", String(siniestroSel.ID));
            formData.append("usuario", String(dataUser?.user?.ID ?? ""));

            const resp = await uploadSiniestroArchivos(siniestroSel.ID, formData);
            const payload = resp?.data ?? resp ?? {};
            const archivosActualizados = Array.isArray(payload.archivos) ? payload.archivos : [];
            if (archivosActualizados.length > 0) {
                setArchivosDinamicos(archivosActualizados.filter(isAdjuntoFisicamenteDisponible));
            } else {
                setArchivosDinamicos([]);
            }
            setArchivosPendientes([]);
            await refreshSiniestroAfterUpdate(siniestroSel.ID, siniestroSel.ESTATUS_SINIESTRO);
            showToast("success", "Archivos guardados", "Los archivos adjuntos se subieron correctamente.");
        } catch {
            showToast("error", "Error", "No se pudieron subir los archivos adjuntos.");
        } finally {
            setSavingAdjuntos(false);
        }
    }, [archivosPendientes, dataUser?.user?.ID, showToast, siniestroSel?.ID]);

    const handleDeleteArchivoDinamico = useCallback(async (archivo) => {
        if (!siniestroSel?.ID) return;
        const archivoId = archivo?.ID ?? archivo?.id;
        if (!archivoId) return;
        setSavingAdjuntos(true);
        try {
            await deleteSiniestroArchivo(siniestroSel.ID, archivoId);
            setArchivosDinamicos((prev) => prev.filter((row) => (row.ID ?? row.id) !== archivoId));
            await refreshSiniestroAfterUpdate(siniestroSel.ID, siniestroSel.ESTATUS_SINIESTRO);
            showToast("success", "Archivo eliminado", "Se eliminó el archivo adjunto.", 2500);
        } catch {
            showToast("error", "Error", "No se pudo eliminar el archivo adjunto.");
        } finally {
            setSavingAdjuntos(false);
        }
    }, [showToast, siniestroSel?.ID]);

    const handleGuardarDescuento = async () => {
        if (!siniestroSel || !descForm.idGrupo || !descForm.DESCUENTO_HERRAMIENTA) return;
        if (!siniestroSel.ID_OPERADOR) {
            showToast("warn", "Operador requerido", "No se puede levantar descuento porque no hay operador asignado.");
            return;
        }
        setSavingDesc(true);
        try {
            const payload = {
                ...(editingDesc ? { ID: siniestroSel.ID_DESCUENTO } : {}),
                FECHA: toIsoUtcStartOfDay(descForm.FECHA_FORMAT) ?? new Date().toISOString(),
                ID_GRUPO_SOLICITA: descForm.idGrupo,
                MONTO: descForm.MONTO,
                NO_DESCUENTOS: descForm.NO_DESCUENTOS != null ? String(descForm.NO_DESCUENTOS) : null,
                MONTO_DESCUENTO: descForm.MONTO_DESCUENTO,
                MOTIVO: descForm.MOTIVO,
                PERIODO_INICIO: descForm.periodoInicio ? String(descForm.periodoInicio) : null,
                ESTATUS: descForm.ESTATUS ?? 1,
                ID_DESCUENTO_HERRAM: descForm.DESCUENTO_HERRAMIENTA,
                ID_CHOFER: siniestroSel.ID_OPERADOR,
                ID_UNIDAD: siniestroSel.ID_UNIDAD ?? null,
                ID_REEMBOLSO: descForm.ID_REEMBOLSO,
                USUARIO: dataUser?.user?.ID,
            };
            let descuentoId = siniestroSel.ID_DESCUENTO;

            if (editingDesc) {
                const respUpd = await updateDescuentoOperador(payload);
                if (respUpd?.success === false) throw new Error("No se pudo actualizar el descuento");
                showToast("success", "Descuento actualizado", "Los cambios del descuento se guardaron correctamente.");
            } else {
                const resp = await insertDescuento(payload);
                const nuevoId = resp?.id ?? resp?.data?.id ?? null;
                if (!nuevoId) throw new Error("No se obtuvo ID del descuento creado");
                await asignarDescuentoASiniestro(siniestroSel.ID, nuevoId);
                descuentoId = nuevoId;
                showToast("success", "Descuento creado", "El descuento fue asignado al siniestro.");
            }

            await refreshSiniestroAfterUpdate(siniestroSel.ID, siniestroSel.ESTATUS_SINIESTRO);

            // Garantizar que la card muestre los datos aunque el JOIN del API
            // use nombres de columna distintos o no los incluya aún
            const _fd = descForm.FECHA_FORMAT;
            const _fechaStr = _fd instanceof Date && !isNaN(_fd)
                ? `${String(_fd.getDate()).padStart(2, '0')}/${String(_fd.getMonth() + 1).padStart(2, '0')}/${_fd.getFullYear()}`
                : (typeof _fd === 'string' ? _fd : "");
            setSiniestroSel((prev) => prev ? {
                ...prev,
                ID_DESCUENTO: descuentoId,
                FECHA_DESCUENTO: prev.FECHA_DESCUENTO || _fechaStr,
                ESTATUS_DESCUENTO: prev.ESTATUS_DESCUENTO ?? (descForm.ESTATUS ?? 1),
                NO_DESCUENTOS: prev.NO_DESCUENTOS ?? descForm.NO_DESCUENTOS,
                MONTO: prev.MONTO ?? descForm.MONTO,
                MONTO_DESCUENTO: prev.MONTO_DESCUENTO ?? descForm.MONTO_DESCUENTO,
                PERIODO_INICIO_DESCUENTO: prev.PERIODO_INICIO_DESCUENTO ?? (descForm.periodoInicio || null),
                MOTIVO_DESCUENTO: prev.MOTIVO_DESCUENTO ?? (descForm.MOTIVO || null),
            } : prev);

            setShowDescForm(false);
            setEditingDesc(false);
            setDescForm(DESCUENTO_FORM_EMPTY);
        } catch (err) {
            console.error("Error al guardar descuento", err);
            showToast("error", "Error", "No se pudo guardar el descuento. Revisa los datos e intenta de nuevo.");
        } finally {
            setSavingDesc(false);
        }
    };

    const handleEditarDescuento = async () => {
        if (!siniestroSel?.ID_DESCUENTO) return;
        if (!siniestroSel.ID_OPERADOR) {
            showToast("warn", "Operador requerido", "No se puede editar descuento porque no hay operador asignado.");
            return;
        }
        try {
            const res = await getDescuentoOperadorById(siniestroSel.ID_DESCUENTO);
            const row = Array.isArray(res) ? res[0] : res;
            if (!row) throw new Error("No se encontró información del descuento");

            setDescForm({
                FECHA_FORMAT: normalizeToDate(row.FECHA_DESCUENTO ?? row.FECHA_FORMAT ?? row.FECHA),
                idGrupo: row.ID_GRUPO_SOLICITA ?? null,
                DESCUENTO_HERRAMIENTA: row.ID_DESCUENTO_HERRAM ?? null,
                MONTO: row.MONTO ?? null,
                DESCUENTO_PORCENTAJE: null,
                NO_DESCUENTOS: row.NO_DESCUENTOS ?? null,
                MONTO_DESCUENTO: row.MONTO_DESCUENTO ?? null,
                periodoInicio: row.PERIODO_INICIO ?? "",
                ID_REEMBOLSO: row.ID_REEMBOLSO ?? null,
                MOTIVO: row.MOTIVO ?? "",
                ESTATUS: row.ESTATUS ?? 1,
            });
            setEditingDesc(true);
            setShowDescForm(true);
            showToast("info", "Editar descuento", "Se cargó la información del descuento para edición.", 2200);
        } catch (err) {
            console.error("Error al cargar descuento para edición", err);
            showToast("error", "Error", "No se pudo cargar la información del descuento.");
        }
    };

    const handleAsignarIncidente = async (idIncidente) => {
        if (!siniestroSel) return;
        setSavingInc(true);
        try {
            await asignarIncidente(idIncidente, siniestroSel.ID);
            await refreshSiniestroAfterUpdate(siniestroSel.ID, siniestroSel.ESTATUS_SINIESTRO);
            showToast("success", "Incidencia asignada", "La incidencia se vinculó al siniestro correctamente.", 2500);
        } catch (err) {
            console.error("Error al asignar incidente", err);
            showToast("error", "Error", "No se pudo asignar la incidencia.");
        } finally {
            setSavingInc(false);
        }
    };

    // ── Edit helpers ──────────────────────────────────────────────────────────
    const setEditField = (field) => (value) => setEditData((p) => ({ ...p, [field]: value }));
    const resolveUsuarioRegistroId = useCallback((data) => {
        const current = data?.USUARIO_REGISTRO_ID;
        const nombre = data?.USUARIO_REGISTRO;

        // Si ya viene un código exacto del catálogo, se usa tal cual.
        const byCode = colaboradores.find((c) => String(c.code) === String(current));
        if (byCode) return byCode.code;

        // Si current trae nombre (caso defectuoso), lo convertimos a code.
        const normalizedCurrent = String(current ?? "").trim().toUpperCase();
        if (normalizedCurrent) {
            const byNameFromCurrent = colaboradores.find(
                (c) => String(c.name ?? "").trim().toUpperCase() === normalizedCurrent
            );
            if (byNameFromCurrent) return byNameFromCurrent.code;
        }

        // Fallback por nombre de usuario mostrado.
        const normalizedNombre = String(nombre ?? "").trim().toUpperCase();
        if (normalizedNombre) {
            const byName = colaboradores.find(
                (c) => String(c.name ?? "").trim().toUpperCase() === normalizedNombre
            );
            if (byName) return byName.code;
        }

        return null;
    }, [colaboradores]);

    const handleStartEdit = () => setEditData({
        ...siniestroSel,
        USUARIO_REGISTRO_ID: resolveUsuarioRegistroId(siniestroSel),
    });
    const handleCancelEdit = () => setEditData(null);

    // Reverse geocoding: convierte coordenadas en nombre de ubicación (Nominatim/OSM)
    const handlePickUbicacion = useCallback(async (lat, lng) => {
        setEditData((p) => ({
            ...p,
            LATITUD: String(lat),
            LONGITUD: String(lng),
            UBICACION: "Buscando dirección...",
        }));
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=es`,
                { headers: { "Accept-Language": "es" } }
            );
            const data = await res.json();
            const nombre = data.display_name
                ?? `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`;
            setEditData((p) => ({ ...p, UBICACION: nombre }));
        } catch {
            setEditData((p) => ({
                ...p,
                UBICACION: `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`,
            }));
        }
    }, []);
    const handleSaveEdit = async () => {
        const tipoColisionSeleccionado = tiposColision.find(
            (option) => String(option.value) === String(editData?.ID_TIPO_COLISION)
        );
        const params = {
            // identificadores
            ID: editData?.ID,
            ESTATUS_SINIESTRO: editData?.ESTATUS_SINIESTRO,
            // información del siniestro
            NUMERO_REPORTE: editData?.NUMERO_REPORTE,
            TIPO_SINIESTRO: editData?.TIPO_SINIESTRO,
            FECHA_SINIESTRO: formateoBasico(editData?.FECHA_SINIESTRO) + " " + extraerHora(editData?.FECHA_SINIESTRO),
            FECHA_REGISTRO: formateoBasico(editData?.FECHA_REGISTRO) + " " + extraerHora(editData?.FECHA_REGISTRO),
            RESPONSABLE_SUMA: editData?.RESPONSABLE_SUMA,
            USUARIO_REGISTRO_ID: resolveUsuarioRegistroId(editData),
            ALCANCE_SEVERIDAD_ID: editData?.ALCANCE_SEVERIDAD_ID,
            DESCRIPCION: editData?.DESCRIPCION,
            // ubicación
            UBICACION: editData?.UBICACION,
            LATITUD: editData?.LATITUD,
            LONGITUD: editData?.LONGITUD,
            // unidad y operador
            TIPO_UNIDAD: editData?.TIPO_UNIDAD,
            ID_OPERADOR: editData?.ID_OPERADOR,
            NOMBRE_CHOFER: editData?.NOMBRE_CHOFER,
            // colisión
            ID_TIPO_COLISION: editData?.ID_TIPO_COLISION,
            TIPO_COLISION: tipoColisionSeleccionado?.label ?? editData?.TIPO_COLISION,
        };
        try {
            await updateAccidentsFull(params);
            const detalleActualizado = await refreshSiniestroAfterUpdate(editData.ID, editData?.ESTATUS_SINIESTRO);
            const datosActualizados = {
                ...(detalleActualizado ?? {}),
                ...editData,
                TIPO_COLISION: params.TIPO_COLISION,
            };
            setSiniestroSel(datosActualizados);
            setSiniestrosCanban((prev) => (prev ?? []).map((row) => (
                String(row.ID) === String(editData.ID) ? { ...row, ...datosActualizados } : row
            )));
            setEditData(null);
            showToast("success", "Siniestro actualizado", "Los cambios del siniestro se guardaron correctamente.", 2500);
        } catch (error) {
            showToast("error", "Error", "No se pudo guardar la edición del siniestro.");
        }
    };

    // ── Render vars ───────────────────────────────────────────────────────────
    const s = siniestroSel;
    const sd = editData ?? s;      // datos activos (edición o lectura)
    const isEditing = editData !== null;
    const folio = s ? `SINIESTRO - ${s.ID}` : "";
    const sev = s ? getSevConfig(s.TIPO_SEVERIDAD) : {};
    const est = s ? getEstatusConfig(s.ESTATUS_SINIESTRO) : {};
    const col = s ? getColConfig(s.ESTATUS_SINIESTRO) : {};
    const fotosCargadasCount = fotoSlots.filter(Boolean).length;
    const fotosPendientesCount = fotoSlots.filter((f) => !!f?.file).length;
    const archivosPendientesCount = archivosPendientes.length;
    const totalPendienteBytes = archivosPendientes.reduce((acc, file) => acc + Number(file?.size ?? 0), 0);

    // Shorthand: campo editable o de lectura
    const ef = (field, label, opts = {}) => (
        <EditableField
            key={field}
            label={label}
            value={sd?.[field]}
            onChange={isEditing ? setEditField(field) : undefined}
            full={opts.full}
            type={opts.type ?? "text"}
            options={opts.options ?? []}
            optionLabel={opts.optionLabel ?? "label"}
            optionValue={opts.optionValue ?? "value"}
        />
    );

    const VIEWS = [
        { id: "kanban", icon: "pi-th-large", label: "Kanban" },
        { id: "tabla", icon: "pi-table", label: "Tabla" },
        { id: "heatmap", icon: "pi-map", label: "Mapa de calor" },
    ];
    return (
        <div className="Siniestro">
            <Toast ref={toast} position="top-right" />

            {/* ── Barra superior: tabs + filtros ──────────────────────────── */}
            <div className="filtros">
                <div className="sin-tabs">
                    {VIEWS.map((v) => (
                        <button
                            key={v.id}
                            className={`sin-tabs__btn${activeView === v.id ? " sin-tabs__btn--active" : ""}`}
                            onClick={() => setActiveView(v.id)}
                        >
                            <i className={`pi ${v.icon}`} />
                            {v.label}
                        </button>
                    ))}
                </div>

                <div style={{ flex: 1 }} />

                <Calendar
                    showIcon
                    value={filtros.fecha_inicio}
                    onChange={(e) => setFiltros((p) => ({ ...p, fecha_inicio: e.value }))}
                    placeholder="Inicio (solo cerrados)"
                    dateFormat="dd/mm/yy"
                />
                <Calendar
                    showIcon
                    value={filtros.fecha_fin}
                    onChange={(e) => setFiltros((p) => ({ ...p, fecha_fin: e.value }))}
                    placeholder="Fin (solo cerrados)"
                    dateFormat="dd/mm/yy"
                />
                <Button icon="pi pi-search" onClick={filtrar} />
            </div>

            {/* ── Sidebar de detalle ───────────────────────────────────────── */}
            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => { setSidebarOpen(false); setEditData(null); }}
                defaultWidth={42}
            >
                {s && (
                    <div className="sin-sidebar">

                        {/* Header */}
                        <div className="sin-sidebar__header" style={{ borderBottomColor: col.color }}>
                            <div className="sin-sidebar__folio">{folio}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span className="sin-sidebar__sev-badge" style={{ color: sev.color, background: sev.bg }}>
                                    <i className={`pi ${sev.icon}`} />
                                    {s.TIPO_SEVERIDAD ?? "Sin severidad"}
                                </span>
                                {isEditing ? (
                                    <>
                                        <Button label="Guardar" icon="pi pi-save" className="p-button-sm p-button-success" onClick={handleSaveEdit} />
                                        <Button icon="pi pi-times" className="p-button-sm p-button-text" onClick={handleCancelEdit} />
                                    </>
                                ) :
                                    (
                                        <Button label="Editar" icon="pi pi-pencil" className="p-button-sm p-button-outlined" onClick={handleStartEdit} />
                                    )
                                }
                            </div>
                        </div>

                        {/* KPI rápido */}
                        <div className="sin-sidebar__kpi-row">
                            <div className="sin-sidebar__kpi">
                                <span className="sin-sidebar__kpi-label">Tipo</span>
                                <span className="sin-sidebar__kpi-value">{s.TIPO_SINIESTRO_DESCRIPCION ?? s.TIPO_SINIESTRO ?? "—"}</span>
                            </div>
                            <div className="sin-sidebar__kpi">
                                <span className="sin-sidebar__kpi-label">Fecha</span>
                                <span className="sin-sidebar__kpi-value">{formatD(s.FECHA_SINIESTRO) || "—"}</span>
                            </div>
                            <div className="sin-sidebar__kpi">
                                <span className="sin-sidebar__kpi-label">Estatus</span>
                                <span className="sin-sidebar__kpi-value" style={{ color: est.color }}>{est.label ?? "—"}</span>
                            </div>
                        </div>

                        {/* ── Info del siniestro ────────────────────────────── */}
                        <div className="sin-sidebar__section">
                            <div className="sin-sidebar__section-title"><i className="pi pi-file-edit" /> Información del siniestro</div>
                            <div className="sin-sidebar__grid">
                                {ef("NUMERO_REPORTE", "Número de reporte")}
                                <EditableField
                                    key="ESTATUS_SINIESTRO"
                                    label="Estatus"
                                    value={isEditing ? sd?.ESTATUS_SINIESTRO : est.label}
                                    onChange={isEditing ? setEditField("ESTATUS_SINIESTRO") : undefined}
                                    type="dropdown"
                                    options={ESTATUS_OPTIONS}
                                />
                                <EditableField
                                    key="TIPO_SINIESTRO"
                                    label="Tipo de siniestro"
                                    value={isEditing ? sd?.TIPO_SINIESTRO : sd?.TIPO_SINIESTRO_DESCRIPCION}
                                    onChange={isEditing ? setEditField("TIPO_SINIESTRO") : undefined}
                                    type="dropdown"
                                    options={tiposSiniestro}
                                    optionLabel="name"
                                    optionValue="code"
                                />
                                <EditableField
                                    key="ID_TIPO_COLISION"
                                    label="Tipo de colisión"
                                    value={isEditing ? resolveTipoColisionValue(sd, tiposColision) : sd?.TIPO_COLISION}
                                    onChange={isEditing ? setEditField("ID_TIPO_COLISION") : undefined}
                                    type="dropdown"
                                    options={tiposColision}
                                    optionLabel="label"
                                    optionValue="value"
                                />
                                <EditableField
                                    key="FECHA_SINIESTRO"
                                    label="Fecha siniestro"
                                    value={isEditing ? sd?.FECHA_SINIESTRO : formatDT(sd?.FECHA_SINIESTRO)}
                                    onChange={isEditing ? setEditField("FECHA_SINIESTRO") : undefined}
                                    type={isEditing ? "date" : "text"}
                                />
                                <EditableField
                                    key="FECHA_REGISTRO"
                                    label="Fecha registro"
                                    value={isEditing ? sd?.FECHA_REGISTRO : formatDT(sd?.FECHA_REGISTRO)}
                                    onChange={isEditing ? setEditField("FECHA_REGISTRO") : undefined}
                                    type={isEditing ? "date" : "text"}
                                />
                                <EditableField
                                    key="RESPONSABLE_SUMA"
                                    label="Responsable SUMA"
                                    value={isEditing ? sd?.RESPONSABLE_SUMA : (sd?.RESPONSABLE_SUMA ? "Sí" : "No")}
                                    onChange={isEditing ? setEditField("RESPONSABLE_SUMA") : undefined}
                                    type={isEditing ? "checkbox" : "text"}
                                />
                                <EditableField
                                    key="USUARIO_REGISTRO"
                                    label="Registrado por"
                                    value={isEditing ? sd?.USUARIO_REGISTRO_ID : sd?.USUARIO_REGISTRO}
                                    onChange={isEditing ? (val) => {
                                        const c = colaboradores.find(x => x.code === val);
                                        setEditData(p => ({ ...p, USUARIO_REGISTRO_ID: val, USUARIO_REGISTRO: c?.name ?? p.USUARIO_REGISTRO }));
                                    } : undefined}
                                    type="dropdown"
                                    options={colaboradores}
                                    optionLabel="name"
                                    optionValue="code"
                                />
                                <EditableField
                                    key="ALCANCE_SEVERIDAD"
                                    label="Alcance severidad"
                                    value={isEditing ? sd?.ALCANCE_SEVERIDAD_ID : sd?.ALCANCE_SEVERIDAD}
                                    onChange={isEditing ? (val) => {
                                        const sv = severidades.find(x => x.code === val);
                                        setEditData(p => ({
                                            ...p,
                                            ALCANCE_SEVERIDAD_ID: val,
                                            ALCANCE_SEVERIDAD: sv?.name ?? p.ALCANCE_SEVERIDAD,
                                            CARACTERISTICA_SEVERIDAD: sv?.CARACTERISTICA ?? p.CARACTERISTICA_SEVERIDAD,
                                            TIPO_SEVERIDAD: sv?.TIPO ?? p.TIPO_SEVERIDAD,
                                        }));
                                    } : undefined}
                                    type="dropdown"
                                    options={severidades}
                                    optionLabel="name"
                                    optionValue="code"
                                />
                                <EditableField key="CARACTERISTICA_SEVERIDAD" label="Característica" value={sd?.CARACTERISTICA_SEVERIDAD} />
                                {ef("DESCRIPCION", "Descripción", { full: true })}
                                {ef("UBICACION", "Ubicación", { full: true })}
                            </div>
                        </div>

                        {/* ── Ubicación en mapa ─────────────────────────────── */}
                        <div className="sin-sidebar__section">
                            <div className="sin-sidebar__section-title"><i className="pi pi-map-marker" /> Ubicación del siniestro</div>
                            {isEditing && (
                                <div style={{ marginBottom: 8 }}>
                                    <label className="sin-field__label">Pegar URL de Google Maps</label>
                                    <InputText
                                        value={mapsUrlInput}
                                        onChange={(e) => {
                                            const url = e.target.value;
                                            setMapsUrlInput(url);
                                            const coords = parseGoogleMapsUrl(url);
                                            if (coords) {
                                                handlePickUbicacion(coords.lat, coords.lng);
                                                setMapsUrlInput("");
                                            }
                                        }}
                                        placeholder="https://www.google.com/maps/..."
                                        className="sin-sidebar__input w-full"
                                    />
                                </div>
                            )}
                            <SidebarMap
                                lat={sd?.LATITUD}
                                lng={sd?.LONGITUD}
                                editable={isEditing}
                                onPick={handlePickUbicacion}
                            />
                            {isEditing && (
                                <div className="sin-sidebar__grid" style={{ marginTop: 8 }}>
                                    {ef("LATITUD", "Latitud")}
                                    {ef("LONGITUD", "Longitud")}
                                </div>
                            )}
                        </div>

                        <Divider />

                        {/* ── Unidad y operador ─────────────────────────────── */}
                        <div className="sin-sidebar__section">
                            <div className="sin-sidebar__section-title"><i className="pi pi-car" /> Unidad y operador</div>
                            <div className="sin-sidebar__grid">
                                <EditableField key="ID_UNIDAD" label="Unidad" value={sd?.ID_UNIDAD} />
                                <EditableField key="TIPO_UNIDAD" label="Tipo de unidad" value={sd?.TIPO_UNIDAD} />
                                <EditableField key="NOMBRE_CHOFER" label="Chofer sugerido" value={sd?.NOMBRE_CHOFER} />
                                <EditableField key="ASIENTOS" label="Asientos" value={sd?.ASIENTOS} />
                                <EditableField
                                    key="OPERADOR_ASIGNADO"
                                    label="Operador asignado"
                                    value={isEditing
                                        ? sd?.ID_OPERADOR
                                        : (sd?.OPERADOR ?? sd?.ID_OPERADOR ?? (
                                            <span style={{ color: "#D13438", fontWeight: 600 }}>Asigna un operador</span>
                                        ))}
                                    onChange={isEditing ? (val) => {
                                        const ch = choferes.find(x => x.code === val);
                                        setEditData(p => ({
                                            ...p,
                                            ID_OPERADOR: val,
                                            OPERADOR: ch?.name ?? p.OPERADOR,
                                        }));
                                    } : undefined}
                                    type="dropdown"
                                    options={choferes}
                                    optionLabel="name"
                                    optionValue="code"
                                />
                                {sd?.LICENCIA != null && <EditableField key="LICENCIA" label="Licencia" value={sd?.LICENCIA} />}
                            </div>
                        </div>

                        <Divider />

                        {/* ── Incidencia ────────────────────────────────────── */}
                        <div className="sin-sidebar__section">
                            <div className="sin-sidebar__section-title"><i className="pi pi-link" /> Incidencia relacionada</div>
                            {s.ID_INCIDENTE ? (
                                <div className="sin-sidebar__info-card sin-sidebar__info-card--linked">
                                    <div className="sin-sidebar__grid">
                                        <EditableField key="ID_INCIDENTE" label="ID Incidencia" value={sd?.ID_INCIDENTE} />
                                        <EditableField key="INCIDENTE_CLIENTE" label="Cliente" value={sd?.INCIDENTE_CLIENTE} />
                                        <EditableField key="CAUSA_INCIDENTE" label="Causa" value={sd?.CAUSA_INCIDENTE} />
                                        <EditableField label="Afecta servicio" value={sd?.AFECTA_SERVICIO_INCIDENTE != null ? (sd.AFECTA_SERVICIO_INCIDENTE ? "Sí" : "No") : null} />
                                        <EditableField key="EFECTO_INCIDENTE" label="Efecto" value={sd?.EFECTO_INCIDENTE} />
                                        <EditableField label="Responsabilidad SUMA" value={sd?.RESPONSABILIDAD_SUMA_INCIDENTE != null ? (sd.RESPONSABILIDAD_SUMA_INCIDENTE ? "Sí" : "No") : null} />
                                        <EditableField key="DETALLE_INCIDENTE" label="Detalle" value={sd?.DETALLE_INCIDENTE} full />
                                    </div>
                                </div>
                            ) : loadingInc ? (
                                <div className="sin-sidebar__empty-state" style={{ flexDirection: "row" }}>
                                    <i className="pi pi-spin pi-spinner sin-sidebar__empty-icon" />
                                    <span className="sin-sidebar__empty-text">Buscando incidencias…</span>
                                </div>
                            ) : incidentesDisponibles.length === 0 ? (
                                <div className="sin-sidebar__empty-state">
                                    <i className="pi pi-minus-circle sin-sidebar__empty-icon" />
                                    <span className="sin-sidebar__empty-text">Sin incidencias para esta fecha y unidad</span>
                                </div>
                            ) : (
                                <div className="sin-inc-list">
                                    {incidentesDisponibles.map((inc) => (
                                        <div key={inc.ID} className="sin-inc-item">
                                            <div className="sin-inc-item__header">
                                                <span className="sin-inc-item__cliente">{inc.CLIENTE ?? "—"}</span>
                                                <span className="sin-inc-item__fecha">{formatD(inc.FECHA_INCIDENTE)}</span>
                                            </div>
                                            <div className="sin-inc-item__chips">
                                                {inc.CAUSA && <span className="sin-inc-item__chip sin-inc-item__chip--causa"><i className="pi pi-exclamation-triangle" />{inc.CAUSA}</span>}
                                                {inc.EFECTO && <span className="sin-inc-item__chip sin-inc-item__chip--efecto"><i className="pi pi-arrow-right" />{inc.EFECTO}</span>}
                                                {inc.RUTA && <span className="sin-inc-item__chip"><i className="pi pi-map" />{inc.RUTA}</span>}
                                                {inc.TURNO && <span className="sin-inc-item__chip"><i className="pi pi-clock" />{inc.TURNO}</span>}
                                            </div>
                                            {inc.DETALLE && <p className="sin-inc-item__detalle">{inc.DETALLE}</p>}
                                            <div className="sin-inc-item__footer">
                                                <Button
                                                    label="Asignar a este siniestro"
                                                    icon="pi pi-link"
                                                    className="p-button-sm p-button-outlined"
                                                    loading={savingInc}
                                                    onClick={() => handleAsignarIncidente(inc.ID)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Divider />

                        {/* ── Descuento ─────────────────────────────────────── */}
                        <div className="sin-sidebar__section">
                            <div className="sin-sidebar__section-title"><i className="pi pi-dollar" /> Descuento al operador</div>
                            {showDescForm ? (
                                <div className="sin-sidebar__desc-form">

                                    {/* Contexto del siniestro — pre-llenado */}
                                    <div className="sin-desc-preview">
                                        <div className="sin-desc-preview__item">
                                            <i className="pi pi-user" />
                                            <span><strong>Operador:</strong> {s.OPERADOR ?? "—"}</span>
                                        </div>
                                        {s.UNIDAD_PLACAS && (
                                            <div className="sin-desc-preview__item">
                                                <i className="pi pi-car" />
                                                <span><strong>Unidad:</strong> {s.UNIDAD_PLACAS}</span>
                                            </div>
                                        )}
                                        <div className="sin-desc-preview__item sin-desc-preview__item--note">
                                            <i className="pi pi-info-circle" />
                                            <span>Los campos con * son obligatorios para guardar</span>
                                        </div>
                                    </div>

                                    <div className="sin-sidebar__grid">

                                        {/* Grupo solicitante */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">Grupo solicitante *</span>
                                            <Dropdown
                                                value={descForm.idGrupo}
                                                options={grupos}
                                                onChange={(e) => setDescForm(p => ({ ...p, idGrupo: e.value }))}
                                                placeholder="Selecciona grupo"
                                                filter
                                                showClear
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* Tipo de descuento */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">Tipo de descuento *</span>
                                            <Dropdown
                                                value={descForm.DESCUENTO_HERRAMIENTA}
                                                options={herramientas}
                                                onChange={(e) => setDescForm(p => ({ ...p, DESCUENTO_HERRAMIENTA: e.value }))}
                                                placeholder="Selecciona tipo"
                                                showClear
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* Fecha */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">Fecha</span>
                                            <Calendar
                                                value={descForm.FECHA_FORMAT}
                                                onChange={(e) => setDescForm(p => ({ ...p, FECHA_FORMAT: e.value }))}
                                                showIcon
                                                dateFormat="dd/mm/yy"
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* Periodo inicio */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">Periodo inicio</span>
                                            <InputText
                                                value={descForm.periodoInicio ?? ""}
                                                onChange={(e) => setDescForm(p => ({ ...p, periodoInicio: e.target.value }))}
                                                placeholder="Ej. 2026/15 (semana 15 de 2026)"
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* Monto total */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">Monto total</span>
                                            <InputNumber
                                                value={descForm.MONTO}
                                                onValueChange={(e) => setDescForm(p => ({ ...p, MONTO: e.value }))}
                                                mode="currency" currency="MXN" locale="es-MX"
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* % descuento — auto-calcula monto */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">% de descuento</span>
                                            <Dropdown
                                                value={descForm.DESCUENTO_PORCENTAJE}
                                                options={DESCUENTO_PORCENTAJE_OPTIONS}
                                                onChange={(e) => setDescForm(p => ({ ...p, DESCUENTO_PORCENTAJE: e.value }))}
                                                placeholder="Ej. 50%"
                                                showClear
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* Monto por descuento (auto-calculado) */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">Monto por descuento <em style={{ fontSize: '0.7rem', color: '#64748b' }}>(auto-calculado)</em></span>
                                            <InputNumber
                                                value={descForm.MONTO_DESCUENTO}
                                                onValueChange={(e) => setDescForm(p => ({ ...p, MONTO_DESCUENTO: e.value }))}
                                                mode="currency" currency="MXN" locale="es-MX"
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* No. de descuentos */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">No. de descuentos</span>
                                            <InputNumber
                                                value={descForm.NO_DESCUENTOS}
                                                onValueChange={(e) => setDescForm(p => ({ ...p, NO_DESCUENTOS: e.value }))}
                                                min={1}
                                                useGrouping={false}
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* ID Reembolso */}
                                        <div className="sin-field">
                                            <span className="sin-field__label">ID Reembolso</span>
                                            <InputNumber
                                                value={descForm.ID_REEMBOLSO}
                                                onValueChange={(e) => setDescForm(p => ({ ...p, ID_REEMBOLSO: e.value }))}
                                                useGrouping={false}
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                        {/* Motivo */}
                                        <div className="sin-field sin-field--full">
                                            <span className="sin-field__label">Motivo del descuento *</span>
                                            <InputText
                                                value={descForm.MOTIVO}
                                                onChange={(e) => setDescForm(p => ({ ...p, MOTIVO: e.target.value }))}
                                                placeholder="Describe el motivo del descuento..."
                                                className="sin-sidebar__input w-full"
                                            />
                                        </div>

                                    </div>

                                    <div className="sin-sidebar__form-actions">
                                        <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-sm"
                                            onClick={() => { setShowDescForm(false); setEditingDesc(false); setDescForm(DESCUENTO_FORM_EMPTY); }} />
                                        <Button label={editingDesc ? "Guardar cambios" : "Guardar descuento"} icon="pi pi-save" className="p-button-sm"
                                            loading={savingDesc}
                                            disabled={!descForm.idGrupo || !descForm.DESCUENTO_HERRAMIENTA}
                                            onClick={handleGuardarDescuento} />
                                    </div>
                                </div>
                            ) : s.ID_DESCUENTO ? (
                                <>
                                    <div className="sin-sidebar__info-card sin-sidebar__info-card--descuento">
                                        <div className="sin-sidebar__grid">
                                            <EditableField key="ID_DESCUENTO" label="ID Descuento" value={sd?.ID_DESCUENTO} />
                                            <EditableField key="ESTATUS_DESCUENTO" label="Estatus" value={sd?.ESTATUS_DESCUENTO} />
                                            <EditableField key="FECHA_DESCUENTO" label="Fecha" value={sd?.FECHA_DESCUENTO} />
                                            <EditableField key="NO_DESCUENTOS" label="No. descuentos" value={sd?.NO_DESCUENTOS} />
                                            <EditableField label="Monto total" value={sd?.MONTO != null ? `$${Number(sd.MONTO).toLocaleString("es-MX", { minimumFractionDigits: 2 })}` : null} />
                                            <EditableField label="Monto por desc." value={sd?.MONTO_DESCUENTO != null ? `$${Number(sd.MONTO_DESCUENTO).toLocaleString("es-MX", { minimumFractionDigits: 2 })}` : null} />
                                            <EditableField key="PERIODO_INICIO_DESCUENTO" label="Periodo inicio" value={sd?.PERIODO_INICIO_DESCUENTO} />
                                            <EditableField key="MOTIVO_DESCUENTO" label="Motivo" value={sd?.MOTIVO_DESCUENTO} full />
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        <Button
                                            label="Editar descuento"
                                            icon="pi pi-pencil"
                                            className="p-button-sm p-button-outlined"
                                            onClick={handleEditarDescuento}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="sin-sidebar__empty-state">
                                    <i className="pi pi-minus-circle sin-sidebar__empty-icon" />
                                    <span className="sin-sidebar__empty-text">Sin descuento asignado</span>
                                    <Button
                                        label="Cargar descuento al operador"
                                        icon="pi pi-plus"
                                        className="p-button-sm sin-sidebar__btn-primary"
                                        onClick={() => {
                                            if (!s.ID_OPERADOR) {
                                                showToast("warn", "Operador requerido", "No se puede levantar descuento porque no hay operador asignado.");
                                                return;
                                            }
                                            setShowDescForm(true);
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <Divider />

                        {/* ── Adjuntos: 4 fotos estáticas + N archivos dinámicos ───── */}
                        <div className="sin-sidebar__section">
                            <div className="sin-sidebar__section-title"><i className="pi pi-images" /> Archivos adjuntos</div>

                            <div className="sin-adjuntos__header-row">
                                <div>
                                    <div className="sin-adjuntos__subheader">Fotografías del siniestro</div>
                                    <p className="sin-adjuntos__helper">Sube hasta 4 fotos (una por espacio). Puedes reemplazar o eliminar cada foto.</p>
                                </div>
                                <div className="sin-adjuntos__badges">
                                    <span className="sin-adjuntos__badge">{fotosCargadasCount}/4 cargadas</span>
                                    {fotosPendientesCount > 0 && <span className="sin-adjuntos__badge sin-adjuntos__badge--pending">{fotosPendientesCount} por guardar</span>}
                                </div>
                            </div>

                            <div className="sin-adjuntos__fotos-grid">
                                {FOTO_SLOTS.map((slot) => {
                                    const foto = fotoSlots?.[slot - 1];
                                    const fotoHref = foto?.previewUrl || foto?.path || "";
                                    return (
                                        <div key={`foto-slot-${slot}`} className="sin-adjuntos__slot-card">
                                            <div className="sin-adjuntos__slot-head">
                                                <div className="sin-adjuntos__slot-title">Foto {slot}</div>
                                                {foto?.file && <span className="sin-adjuntos__chip sin-adjuntos__chip--draft">Pendiente</span>}
                                                {!foto?.file && foto && <span className="sin-adjuntos__chip sin-adjuntos__chip--saved">Guardada</span>}
                                            </div>
                                            {foto ? (
                                                <>
                                                    <div className="sin-adjuntos__thumb-wrap">
                                                        {fotoHref ? (
                                                            <img className="sin-adjuntos__thumb" src={fotoHref} alt={foto.name || `Foto ${slot}`} />
                                                        ) : (
                                                            <div className="sin-adjuntos__thumb sin-adjuntos__thumb--fallback">
                                                                <i className="pi pi-image" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="sin-adjuntos__file-meta">
                                                        <span className="sin-adjuntos__file-name" title={foto.name || `Foto ${slot}`}>
                                                            {foto.name || `Foto ${slot}`}
                                                        </span>
                                                        {fotoHref && (
                                                            <a
                                                                href={fotoHref}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="sin-adjuntos__file-link"
                                                            >
                                                                Ver imagen
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className="sin-adjuntos__slot-actions">
                                                        <label className="sin-adjuntos__upload-btn">
                                                            <i className="pi pi-refresh" /> Reemplazar
                                                            <input
                                                                type="file"
                                                                accept={ACCEPTED_FOTOS}
                                                                onChange={(e) => {
                                                                    onSelectFoto(slot, e.target.files?.[0]);
                                                                    e.target.value = "";
                                                                }}
                                                            />
                                                        </label>
                                                        <Button
                                                            label="Eliminar"
                                                            icon="pi pi-trash"
                                                            className="p-button-text p-button-sm"
                                                            onClick={() => handleEliminarFoto(slot)}
                                                            disabled={savingAdjuntos}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="sin-adjuntos__slot-empty"><i className="pi pi-image" /> Sin imagen</div>
                                                    <label className="sin-adjuntos__upload-btn">
                                                        <i className="pi pi-upload" /> Subir foto
                                                        <input
                                                            type="file"
                                                            accept={ACCEPTED_FOTOS}
                                                            onChange={(e) => {
                                                                onSelectFoto(slot, e.target.files?.[0]);
                                                                e.target.value = "";
                                                            }}
                                                        />
                                                    </label>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="sin-adjuntos__note">Formatos permitidos: JPG, JPEG, PNG, WEBP.</div>
                            <div className="sin-adjuntos__actions-row">
                                <Button
                                    label={fotosPendientesCount > 0 ? `Guardar ${fotosPendientesCount} foto(s)` : "Guardar fotos"}
                                    icon="pi pi-save"
                                    className="p-button-sm"
                                    onClick={handleGuardarFotos}
                                    loading={savingAdjuntos}
                                    disabled={adjuntosLoading || fotosPendientesCount === 0}
                                />
                            </div>

                            <Divider />

                            <div className="sin-adjuntos__header-row">
                                <div>
                                    <div className="sin-adjuntos__subheader">Archivos adicionales</div>
                                    <p className="sin-adjuntos__helper">Adjunta 1 o varios documentos o videos relacionados al siniestro.</p>
                                </div>
                                <div className="sin-adjuntos__badges">
                                    <span className="sin-adjuntos__badge">{archivosDinamicos.length} guardados</span>
                                    {archivosPendientesCount > 0 && (
                                        <span className="sin-adjuntos__badge sin-adjuntos__badge--pending">
                                            {archivosPendientesCount} pendientes · {formatFileSize(totalPendienteBytes)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="sin-adjuntos__dynamic-upload">
                                <label className="sin-adjuntos__upload-btn sin-adjuntos__upload-btn--wide">
                                    <i className="pi pi-paperclip" /> Seleccionar uno o varios archivos
                                    <input
                                        type="file"
                                        multiple
                                        accept={ACCEPTED_ARCHIVOS}
                                        onChange={(e) => {
                                            handleAddArchivosPendientes(e.target.files);
                                            e.target.value = "";
                                        }}
                                    />
                                </label>
                                <Button
                                    label={archivosPendientesCount > 0 ? `Subir ${archivosPendientesCount} archivo(s)` : "Subir archivos"}
                                    icon="pi pi-upload"
                                    className="p-button-sm p-button-outlined"
                                    onClick={handleUploadArchivos}
                                    loading={savingAdjuntos}
                                    disabled={adjuntosLoading || archivosPendientes.length === 0}
                                />
                            </div>
                            <div className="sin-adjuntos__note">Formatos permitidos: PDF, XLS/XLSX, DOC/DOCX, CSV, TXT, MP4, MOV, AVI.</div>

                            {archivosPendientes.length > 0 && (
                                <div className="sin-adjuntos__pending-list">
                                    {archivosPendientes.map((file, idx) => (
                                        <div key={`${file.name}-${idx}`} className="sin-adjuntos__file-item">
                                            <div className="sin-adjuntos__file-meta">
                                                <span className="sin-adjuntos__file-name">{file.name}</span>
                                                <span className="sin-adjuntos__file-size">{formatFileSize(file.size)}</span>
                                            </div>
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-text p-button-sm"
                                                onClick={() => setArchivosPendientes((prev) => prev.filter((_, i) => i !== idx))}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {archivoAnalisis && (
                                <div className="sin-adjuntos__legacy">
                                    <span className="sin-adjuntos__legacy-label">Archivo análisis legado</span>
                                    {getAdjuntoHref(archivoAnalisis) ? (
                                        <a href={getAdjuntoHref(archivoAnalisis)} target="_blank" rel="noreferrer" className="sin-adjuntos__file-link">
                                            {archivoAnalisis?.name ?? archivoAnalisis?.nombre ?? getFileNameFromPath(archivoAnalisis?.path ?? archivoAnalisis?.ruta ?? archivoAnalisis?.archivo)}
                                        </a>
                                    ) : (
                                        <span className="sin-adjuntos__file-name">No disponible físicamente</span>
                                    )}
                                </div>
                            )}

                            {archivosDinamicos.length === 0 ? (
                                <div className="sin-adjuntos__empty-dynamic">
                                    <i className="pi pi-folder-open" />
                                    <span>Sin archivos adicionales cargados.</span>
                                </div>
                            ) : (
                                <div className="sin-adjuntos__dynamic-list">
                                    {archivosDinamicos.map((archivo) => {
                                        const path = archivo.ARCHIVO ?? archivo.path ?? archivo.ruta ?? archivo.archivo ?? "";
                                        const href = getAdjuntoHref(archivo);
                                        const idArchivo = archivo.ID ?? archivo.id;
                                        return (
                                            <div key={idArchivo} className="sin-adjuntos__file-item">
                                                <div className="sin-adjuntos__file-meta">
                                                    {href ? (
                                                        <a href={href} target="_blank" rel="noreferrer" className="sin-adjuntos__file-link">
                                                            {archivo?.name ?? archivo?.nombre ?? getFileNameFromPath(path)}
                                                        </a>
                                                    ) : (
                                                        <span className="sin-adjuntos__file-name">{archivo?.name ?? archivo?.nombre ?? getFileNameFromPath(path)}</span>
                                                    )}
                                                    <span className="sin-adjuntos__file-size">ID {idArchivo}</span>
                                                </div>
                                                <Button
                                                    icon="pi pi-trash"
                                                    className="p-button-text p-button-sm"
                                                    onClick={() => handleDeleteArchivoDinamico(archivo)}
                                                    disabled={savingAdjuntos}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                    </div>
                )}
            </Sidebar>

            {/* ── Vistas principales ───────────────────────────────────────── */}
            {activeView === "kanban" && (
                <DragAndDrop
                    className="canban"
                    lists={listas}
                    onChange={setListas}
                    onItemMove={handleItemMove}
                    renderItem={(item) => (
                        <div onClick={() => handleVerDetalle(item)} style={{ cursor: "pointer" }}>
                            <SiniestroCard item={item} />
                        </div>
                    )}
                />
            )}

            {activeView === "tabla" && (
                <TablaView siniestros={siniestrosCanban} onVerDetalle={handleVerDetalle} />
            )}

            {activeView === "heatmap" && (
                <HeatmapView siniestros={siniestrosCanban} />
            )}

        </div>
    );
};

export default Siniestros;

